import { useState, useEffect, useRef, useCallback } from 'react'
import {
  ArrowLeft, Activity, Cpu, Database, HardDrive, Loader2,
  Copy, Check, AlertCircle, Zap, Brain, Rocket, Server,
  ChevronDown, CircleDot, Layers, GitBranch, BarChart3,
  Clock, Box, Gauge, Terminal, ExternalLink,
} from 'lucide-react'
import * as echarts from 'echarts'
import './ModelTrainingView.css'

const SLM_BASE = 'http://localhost:8400'

// ── Known model specs (shown even when offline) ──
const MODEL_SPECS = {
  name: 'StarCoder2 7B',
  fullName: 'bigcode/starcoder2-7b',
  parameters: 7_188_603_904,
  loraTrainable: 14_680_064,
  architecture: 'Transformer (decoder-only)',
  context: '16,384 tokens',
  precision: 'FP16',
  device: 'MPS',
  languagePair: 'COBOL → Java',
  technique: 'LoRA (Low-Rank Adaptation)',
}

const TRAINING_CONFIG = {
  loraRank: 16,
  loraAlpha: 32,
  learningRate: 2e-4,
  batchSize: 1,
  gradAccum: 8,
  effectiveBatch: 8,
  epochs: 3,
  maxSeqLen: 768,
  optimizer: 'AdamW',
  scheduler: 'Cosine',
  warmupRatio: 0.03,
  weightDecay: 0.01,
}

const DATA_STATS = {
  train: 12_565,
  valid: 1_623,
  test: 1_269,
  total: 15_457,
}

function ModelTrainingView({ onBack, onOpenSettings, onOpenHelp, userProfile }) {
  const [status, setStatus] = useState(null)
  const [trainingRuns, setTrainingRuns] = useState([])
  const [evaluations, setEvaluations] = useState([])
  const [loading, setLoading] = useState(true)
  const [offline, setOffline] = useState(false)
  const [copiedId, setCopiedId] = useState(null)
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  // --- Fetch dashboard data ---
  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${SLM_BASE}/dashboard/status`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setStatus(data)
      setOffline(false)
    } catch {
      setOffline(true)
      setStatus(null)
    }
  }, [])

  const fetchRuns = useCallback(async () => {
    try {
      const res = await fetch(`${SLM_BASE}/dashboard/training-runs`)
      if (res.ok) {
        const data = await res.json()
        setTrainingRuns(data.runs || [])
      }
    } catch { /* offline */ }
  }, [])

  const fetchEvals = useCallback(async () => {
    try {
      const res = await fetch(`${SLM_BASE}/dashboard/evaluations`)
      if (res.ok) {
        const data = await res.json()
        setEvaluations(data.evaluations || [])
      }
    } catch { /* offline */ }
  }, [])

  // Initial fetch
  useEffect(() => {
    const init = async () => {
      setLoading(true)
      await Promise.all([fetchStatus(), fetchRuns(), fetchEvals()])
      setLoading(false)
    }
    init()
  }, [fetchStatus, fetchRuns, fetchEvals])

  // Polling — 5s when training, 30s when idle
  useEffect(() => {
    const isTraining = status?.training?.status === 'training'
    const interval = setInterval(fetchStatus, isTraining ? 5000 : 30000)
    return () => clearInterval(interval)
  }, [status?.training?.status, fetchStatus])

  // --- ECharts loss curve ---
  useEffect(() => {
    if (!chartRef.current) return
    const logHistory = status?.training?.log_history
    if (!logHistory || logHistory.length < 2) {
      if (chartInstance.current) {
        chartInstance.current.dispose()
        chartInstance.current = null
      }
      return
    }

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current)
    }

    const trainSteps = []
    const trainLoss = []
    const evalSteps = []
    const evalLoss = []

    for (const entry of logHistory) {
      if (entry.loss != null) {
        trainSteps.push(entry.step)
        trainLoss.push(entry.loss)
      }
      if (entry.eval_loss != null) {
        evalSteps.push(entry.step)
        evalLoss.push(entry.eval_loss)
      }
    }

    const option = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#21262d',
        borderColor: '#30363d',
        textStyle: { color: '#c9d1d9', fontSize: 12 },
      },
      legend: {
        data: ['Training Loss', 'Eval Loss'],
        textStyle: { color: '#8b949e', fontSize: 11 },
        top: 8,
        right: 16,
      },
      grid: { top: 40, right: 24, bottom: 32, left: 56 },
      xAxis: {
        type: 'category',
        data: trainSteps,
        axisLine: { lineStyle: { color: '#30363d' } },
        axisLabel: { color: '#6e7681', fontSize: 10 },
        name: 'Step',
        nameTextStyle: { color: '#6e7681', fontSize: 10 },
      },
      yAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: '#30363d' } },
        axisLabel: { color: '#6e7681', fontSize: 10 },
        splitLine: { lineStyle: { color: '#21262d' } },
        name: 'Loss',
        nameTextStyle: { color: '#6e7681', fontSize: 10 },
      },
      series: [
        {
          name: 'Training Loss',
          type: 'line',
          data: trainLoss,
          smooth: true,
          showSymbol: false,
          lineStyle: { color: '#38bdf8', width: 2 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(56,189,248,0.2)' },
              { offset: 1, color: 'rgba(56,189,248,0)' },
            ]),
          },
        },
        ...(evalLoss.length > 0 ? [{
          name: 'Eval Loss',
          type: 'line',
          data: evalSteps.map((step, i) => [trainSteps.indexOf(step) >= 0 ? trainSteps.indexOf(step) : step, evalLoss[i]]),
          smooth: true,
          showSymbol: true,
          symbolSize: 6,
          lineStyle: { color: '#f59e0b', width: 2, type: 'dashed' },
          itemStyle: { color: '#f59e0b' },
        }] : []),
      ],
    }

    chartInstance.current.setOption(option)

    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [status?.training?.log_history])

  // Cleanup chart on unmount
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose()
        chartInstance.current = null
      }
    }
  }, [])

  // --- Copy to clipboard ---
  const copyText = (text, id) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  // --- Helpers ---
  const formatNumber = (n) => {
    if (n == null) return '--'
    if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`
    if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`
    if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`
    return n.toLocaleString()
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '--'
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    if (h > 0) return `${h}h ${m}m`
    return `${m}m`
  }

  const getModelStatus = () => {
    if (offline) return { label: 'Offline', cls: 'offline' }
    if (status?.training?.status === 'training') return { label: 'Training', cls: 'training' }
    if (status?.model?.loaded) return { label: 'Online', cls: 'online' }
    return { label: 'Degraded', cls: 'offline' }
  }

  // --- Render ---
  if (loading) {
    return (
      <div className="mt-view">
        <div className="mt-loading">
          <Loader2 size={20} />
          <span>Loading dashboard...</span>
        </div>
      </div>
    )
  }

  const modelStatus = getModelStatus()
  const training = status?.training
  const model = status?.model || {}
  const config = status?.config || {}
  const dataStats = status?.data || {}
  const latestEval = evaluations.length > 0 ? evaluations[0] : null

  const progressPct = training?.total_steps
    ? ((training.current_step / training.total_steps) * 100).toFixed(1)
    : 0

  // Use API data when available, fall back to known specs
  const totalParams = model.parameters || MODEL_SPECS.parameters
  const trainableParams = model.trainable_parameters || MODEL_SPECS.loraTrainable
  const device = model.device ? model.device.toUpperCase() : MODEL_SPECS.device
  const totalSamples = dataStats.total_samples || DATA_STATS.total

  return (
    <div className="mt-view">
      {/* Header */}
      <div className="mt-header">
        <div className="mt-header-left">
          <button className="mt-back-btn" onClick={onBack}>
            <ArrowLeft size={16} />
            Back
          </button>
          <div className="mt-title-group">
            <div className="mt-title">Model Training Dashboard</div>
            <div className="mt-subtitle">
              {MODEL_SPECS.name} &middot; {MODEL_SPECS.languagePair}
            </div>
          </div>
        </div>
        <div className="mt-header-right">
          <button className="mt-action-btn primary" title="Coming soon" disabled>
            <Zap size={14} />
            Start Training
          </button>
          <button className="mt-action-btn" title="Coming soon" disabled>
            <Rocket size={14} />
            Deploy Adapter
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="mt-content">
        <div className="mt-content-inner">

          {/* ── MODEL CARD (hero) ── */}
          <div className="mt-model-card">
            <div className="mt-model-card-left">
              <div className="mt-model-icon">
                <Brain size={28} />
              </div>
              <div className="mt-model-info">
                <div className="mt-model-name">{MODEL_SPECS.name}</div>
                <div className="mt-model-fullname">{MODEL_SPECS.fullName}</div>
                <div className="mt-model-tags">
                  <span className="mt-tag blue">{MODEL_SPECS.languagePair}</span>
                  <span className="mt-tag green">{MODEL_SPECS.technique}</span>
                  <span className="mt-tag">{MODEL_SPECS.precision}</span>
                </div>
              </div>
            </div>
            <div className="mt-model-card-right">
              <div className="mt-model-stat">
                <span className="mt-model-stat-value">{formatNumber(totalParams)}</span>
                <span className="mt-model-stat-label">Parameters</span>
              </div>
              <div className="mt-model-stat-divider" />
              <div className="mt-model-stat">
                <span className="mt-model-stat-value accent">{formatNumber(trainableParams)}</span>
                <span className="mt-model-stat-label">Trainable (LoRA)</span>
              </div>
              <div className="mt-model-stat-divider" />
              <div className="mt-model-stat">
                <span className="mt-model-stat-value">{device}</span>
                <span className="mt-model-stat-label">Device</span>
              </div>
              <div className="mt-model-stat-divider" />
              <div className="mt-model-stat">
                <span className={`mt-model-stat-value ${modelStatus.cls}`}>
                  <span className={`mt-status-dot ${modelStatus.cls}`} />
                  {modelStatus.label}
                </span>
                <span className="mt-model-stat-label">Service</span>
              </div>
            </div>
          </div>

          {/* Offline info bar */}
          {offline && (
            <div className="mt-offline-banner">
              <Server size={16} />
              <span>SLM Layer service is not running. Model specs shown from configuration.</span>
              <code className="mt-offline-cmd">cd modernizeit-slm-layer && uvicorn main:app --port 8400</code>
            </div>
          )}

          {/* ── TWO COLUMN GRID ── */}
          <div className="mt-grid-2">

            {/* Left: Training + Training Config */}
            <div className="mt-grid-col">
              {/* ── TRAINING PROGRESS ── */}
              <div className="mt-section">
                <div className="mt-section-header">
                  <Activity size={14} className="mt-section-icon" />
                  <span className="mt-section-title">Training</span>
                  <div className="mt-section-line" />
                </div>

                {training ? (
                  <div className="mt-progress-box">
                    <div className="mt-progress-top">
                      <div className="mt-progress-label">
                        Epoch {training.current_epoch || 0}/{training.total_epochs || 3}
                        {' '}&middot;{' '}
                        Step {(training.current_step || 0).toLocaleString()}/{(training.total_steps || 0).toLocaleString()}
                      </div>
                      <div className="mt-progress-pct">{progressPct}%</div>
                    </div>

                    <div className="mt-progress-bar-track">
                      <div
                        className={`mt-progress-bar-fill ${training.status === 'training' ? 'animated' : ''}`}
                        style={{ width: `${Math.min(progressPct, 100)}%` }}
                      />
                    </div>

                    <div className="mt-progress-stats">
                      <div className="mt-progress-stat">
                        Loss: <strong>{training.loss != null ? training.loss.toFixed(4) : '--'}</strong>
                      </div>
                      <div className="mt-progress-stat">
                        LR: <strong>{training.learning_rate != null ? training.learning_rate.toExponential(1) : '--'}</strong>
                      </div>
                      <div className="mt-progress-stat">
                        Step rate: <strong>{training.step_rate_seconds ? `~${training.step_rate_seconds.toFixed(1)}s/step` : '--'}</strong>
                      </div>
                      <div className="mt-progress-stat">
                        Elapsed: <strong>{formatDuration(training.elapsed_seconds)}</strong>
                      </div>
                      <div className="mt-progress-stat">
                        Status: <strong style={{
                          color: training.status === 'training' ? 'var(--accent-blue)'
                               : training.status === 'completed' ? 'var(--accent-green)'
                               : training.status === 'failed' ? 'var(--accent-red)'
                               : 'var(--text-muted)'
                        }}>{training.status}</strong>
                      </div>
                      {training.run_name && (
                        <div className="mt-progress-stat">
                          Run: <strong>{training.run_name}</strong>
                        </div>
                      )}
                    </div>

                    {/* Loss curve chart */}
                    {training.log_history && training.log_history.length >= 2 && (
                      <div className="mt-chart-container" ref={chartRef} />
                    )}
                  </div>
                ) : (
                  <div className="mt-waiting-box">
                    <div className="mt-waiting-icon">
                      <Activity size={20} />
                    </div>
                    <div className="mt-waiting-text">
                      <div className="mt-waiting-title">Awaiting Training Data</div>
                      <div className="mt-waiting-desc">
                        Live training metrics will appear here when a training run is active.
                        Progress updates every 5 seconds including loss curve, learning rate, and step timing.
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── TRAINING CONFIGURATION ── */}
              <div className="mt-section">
                <div className="mt-section-header">
                  <Gauge size={14} className="mt-section-icon" />
                  <span className="mt-section-title">Hyperparameters</span>
                  <div className="mt-section-line" />
                </div>

                <div className="mt-config-grid">
                  {[
                    { label: 'LoRA Rank', value: config.lora_rank || TRAINING_CONFIG.loraRank, icon: Layers },
                    { label: 'LoRA Alpha', value: config.lora_alpha || TRAINING_CONFIG.loraAlpha, icon: Layers },
                    { label: 'Learning Rate', value: Number(config.learning_rate || TRAINING_CONFIG.learningRate).toExponential(0), icon: BarChart3 },
                    { label: 'Batch Size', value: config.batch_size || TRAINING_CONFIG.batchSize, icon: Box },
                    { label: 'Grad Accum', value: config.gradient_accumulation_steps || TRAINING_CONFIG.gradAccum, icon: GitBranch },
                    { label: 'Effective Batch', value: (config.batch_size || TRAINING_CONFIG.batchSize) * (config.gradient_accumulation_steps || TRAINING_CONFIG.gradAccum), icon: Box, accent: true },
                    { label: 'Max Seq Length', value: TRAINING_CONFIG.maxSeqLen, icon: Activity },
                    { label: 'Epochs', value: config.num_epochs || TRAINING_CONFIG.epochs, icon: Clock },
                    { label: 'Optimizer', value: TRAINING_CONFIG.optimizer, icon: Cpu },
                    { label: 'Scheduler', value: TRAINING_CONFIG.scheduler, icon: BarChart3 },
                    { label: 'Warmup Ratio', value: TRAINING_CONFIG.warmupRatio, icon: Zap },
                    { label: 'Weight Decay', value: TRAINING_CONFIG.weightDecay, icon: Activity },
                  ].map(({ label, value, icon: Icon, accent }) => (
                    <div className="mt-config-item" key={label}>
                      <Icon size={13} className="mt-config-icon" />
                      <span className="mt-config-label">{label}</span>
                      <span className={`mt-config-value ${accent ? 'accent' : ''}`}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Dataset + Architecture + Evaluation */}
            <div className="mt-grid-col">
              {/* ── DATASET ── */}
              <div className="mt-section">
                <div className="mt-section-header">
                  <Database size={14} className="mt-section-icon" />
                  <span className="mt-section-title">Dataset</span>
                  <div className="mt-section-line" />
                </div>

                <div className="mt-dataset-box">
                  <div className="mt-dataset-total">
                    <span className="mt-dataset-total-value">{formatNumber(totalSamples)}</span>
                    <span className="mt-dataset-total-label">Total Translation Pairs</span>
                  </div>
                  <div className="mt-dataset-splits">
                    <div className="mt-dataset-split">
                      <div className="mt-dataset-bar-label">
                        <span>Train</span>
                        <span className="mt-dataset-count">{(dataStats.train_samples || DATA_STATS.train).toLocaleString()}</span>
                      </div>
                      <div className="mt-dataset-bar">
                        <div className="mt-dataset-bar-fill train" style={{ width: `${((dataStats.train_samples || DATA_STATS.train) / (totalSamples)) * 100}%` }} />
                      </div>
                    </div>
                    <div className="mt-dataset-split">
                      <div className="mt-dataset-bar-label">
                        <span>Validation</span>
                        <span className="mt-dataset-count">{(dataStats.valid_samples || DATA_STATS.valid).toLocaleString()}</span>
                      </div>
                      <div className="mt-dataset-bar">
                        <div className="mt-dataset-bar-fill valid" style={{ width: `${((dataStats.valid_samples || DATA_STATS.valid) / (totalSamples)) * 100}%` }} />
                      </div>
                    </div>
                    <div className="mt-dataset-split">
                      <div className="mt-dataset-bar-label">
                        <span>Test</span>
                        <span className="mt-dataset-count">{(dataStats.test_samples || DATA_STATS.test).toLocaleString()}</span>
                      </div>
                      <div className="mt-dataset-bar">
                        <div className="mt-dataset-bar-fill test" style={{ width: `${((dataStats.test_samples || DATA_STATS.test) / (totalSamples)) * 100}%` }} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-dataset-note">
                    Source: AST-to-AST translated COBOL-85 test suite ({MODEL_SPECS.languagePair})
                  </div>
                </div>
              </div>

              {/* ── MODEL ARCHITECTURE ── */}
              <div className="mt-section">
                <div className="mt-section-header">
                  <Cpu size={14} className="mt-section-icon" />
                  <span className="mt-section-title">Architecture</span>
                  <div className="mt-section-line" />
                </div>

                <div className="mt-arch-box">
                  <div className="mt-arch-flow">
                    <div className="mt-arch-node base">
                      <Brain size={16} />
                      <span className="mt-arch-node-name">StarCoder2 7B</span>
                      <span className="mt-arch-node-detail">Frozen base weights</span>
                    </div>
                    <div className="mt-arch-arrow" />
                    <div className="mt-arch-node lora">
                      <Layers size={16} />
                      <span className="mt-arch-node-name">LoRA Adapter</span>
                      <span className="mt-arch-node-detail">r={TRAINING_CONFIG.loraRank}, α={TRAINING_CONFIG.loraAlpha}</span>
                    </div>
                    <div className="mt-arch-arrow" />
                    <div className="mt-arch-node output">
                      <Zap size={16} />
                      <span className="mt-arch-node-name">Java Output</span>
                      <span className="mt-arch-node-detail">Method bodies</span>
                    </div>
                  </div>
                  <div className="mt-arch-specs">
                    <div className="mt-arch-spec">
                      <span className="mt-arch-spec-label">Architecture</span>
                      <span className="mt-arch-spec-value">{MODEL_SPECS.architecture}</span>
                    </div>
                    <div className="mt-arch-spec">
                      <span className="mt-arch-spec-label">Context Window</span>
                      <span className="mt-arch-spec-value">{MODEL_SPECS.context}</span>
                    </div>
                    <div className="mt-arch-spec">
                      <span className="mt-arch-spec-label">Precision</span>
                      <span className="mt-arch-spec-value">{MODEL_SPECS.precision}</span>
                    </div>
                    <div className="mt-arch-spec">
                      <span className="mt-arch-spec-label">LoRA Targets</span>
                      <span className="mt-arch-spec-value">q_proj, k_proj, v_proj, o_proj</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── EVALUATION METRICS ── */}
              <div className="mt-section">
                <div className="mt-section-header">
                  <BarChart3 size={14} className="mt-section-icon" />
                  <span className="mt-section-title">Evaluation Metrics</span>
                  <div className="mt-section-line" />
                </div>

                {latestEval ? (
                  <>
                    <div className="mt-cards">
                      <div className="mt-card">
                        <div className={`mt-card-value ${latestEval.mean_bleu > 0.5 ? 'green' : 'yellow'}`}>
                          {latestEval.mean_bleu?.toFixed(3) || '--'}
                        </div>
                        <div className="mt-card-label">BLEU-4</div>
                      </div>
                      <div className="mt-card">
                        <div className={`mt-card-value ${latestEval.compilation_rate > 0.8 ? 'green' : 'yellow'}`}>
                          {latestEval.compilation_rate != null ? `${(latestEval.compilation_rate * 100).toFixed(1)}%` : '--'}
                        </div>
                        <div className="mt-card-label">Compile Rate</div>
                      </div>
                      <div className="mt-card">
                        <div className="mt-card-value">
                          {latestEval.exact_match_rate != null ? `${(latestEval.exact_match_rate * 100).toFixed(1)}%` : '--'}
                        </div>
                        <div className="mt-card-label">Exact Match</div>
                      </div>
                      <div className="mt-card">
                        <div className={`mt-card-value ${latestEval.mean_token_f1 > 0.7 ? 'green' : 'yellow'}`}>
                          {latestEval.mean_token_f1?.toFixed(3) || '--'}
                        </div>
                        <div className="mt-card-label">Token F1</div>
                      </div>
                      <div className="mt-card">
                        <div className="mt-card-value">
                          {latestEval.mean_edit_ratio?.toFixed(3) || '--'}
                        </div>
                        <div className="mt-card-label">Edit Ratio</div>
                      </div>
                      <div className="mt-card">
                        <div className="mt-card-value blue">
                          {latestEval.p50_latency_ms != null ? `${Math.round(latestEval.p50_latency_ms)}ms` : '--'}
                        </div>
                        <div className="mt-card-label">Latency (p50)</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -8 }}>
                      {latestEval.total_samples} samples evaluated
                      {latestEval.run_name && ` \u00B7 ${latestEval.run_name}`}
                    </div>
                  </>
                ) : (
                  <div className="mt-waiting-box compact">
                    <div className="mt-waiting-icon small">
                      <BarChart3 size={16} />
                    </div>
                    <div className="mt-waiting-text">
                      <div className="mt-waiting-title">Pending Evaluation</div>
                      <div className="mt-waiting-desc">
                        BLEU-4, compilation rate, exact match, and token F1 metrics will appear after running the evaluation script on test data.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── ADAPTER HISTORY (full width) ── */}
          <div className="mt-section">
            <div className="mt-section-header">
              <HardDrive size={14} className="mt-section-icon" />
              <span className="mt-section-title">Adapter History</span>
              <div className="mt-section-line" />
            </div>

            {trainingRuns.length > 0 ? (
              <div className="mt-table-wrap">
                <table className="mt-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Source</th>
                      <th>Train Loss</th>
                      <th>Eval Loss</th>
                      <th>Perplexity</th>
                      <th>Epochs</th>
                      <th>LoRA Rank</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trainingRuns.map((run, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{run.name}</td>
                        <td>{run.source}</td>
                        <td>{run.train_loss?.toFixed(4) || '--'}</td>
                        <td>{run.eval_loss?.toFixed(4) || '--'}</td>
                        <td>{run.eval_perplexity?.toFixed(2) || '--'}</td>
                        <td>{run.num_epochs || '--'}</td>
                        <td>{run.lora_rank || '--'}</td>
                        <td>
                          <span className={`mt-badge ${model.adapter?.includes(run.name) ? 'active' : 'available'}`}>
                            {model.adapter?.includes(run.name) ? 'Active' : 'Available'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mt-waiting-box compact">
                <div className="mt-waiting-icon small">
                  <HardDrive size={16} />
                </div>
                <div className="mt-waiting-text">
                  <div className="mt-waiting-title">No Trained Adapters</div>
                  <div className="mt-waiting-desc">
                    Completed training runs will appear here with loss metrics, perplexity, and deployment status.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── API REFERENCE (full width) ── */}
          <div className="mt-section">
            <div className="mt-section-header">
              <Terminal size={14} className="mt-section-icon" />
              <span className="mt-section-title">API Reference</span>
              <div className="mt-section-line" />
            </div>

            <div className="mt-api-section">
              <div className="mt-api-url">
                <span className="mt-api-label">Base URL</span>
                <span>{SLM_BASE}</span>
                <button
                  className={`mt-copy-btn ${copiedId === 'base-url' ? 'copied' : ''}`}
                  onClick={() => copyText(SLM_BASE, 'base-url')}
                >
                  {copiedId === 'base-url' ? <Check size={12} /> : <Copy size={12} />}
                  {copiedId === 'base-url' ? 'Copied' : 'Copy'}
                </button>
              </div>

              <div className="mt-api-grid">
                {[
                  { method: 'POST', path: '/translate', desc: 'Translate COBOL paragraph to Java method body' },
                  { method: 'GET', path: '/health', desc: 'Model status and health check' },
                  { method: 'GET', path: '/models', desc: 'Model info, parameters, active adapter' },
                  { method: 'POST', path: '/models/swap', desc: 'Hot-swap LoRA adapter' },
                  { method: 'POST', path: '/evaluate', desc: 'Evaluate translation quality' },
                ].map((ep, i) => (
                  <div className="mt-api-endpoint" key={i}>
                    <span className={`mt-api-method ${ep.method.toLowerCase()}`}>{ep.method}</span>
                    <span className="mt-api-path">{ep.path}</span>
                    <span className="mt-api-desc">{ep.desc}</span>
                  </div>
                ))}
              </div>

              {/* Example request */}
              <div className="mt-code-block">
                <div className="mt-code-header">
                  <span>Example: Translate Request</span>
                  <button
                    className={`mt-copy-btn ${copiedId === 'example' ? 'copied' : ''}`}
                    onClick={() => copyText(exampleCode, 'example')}
                  >
                    {copiedId === 'example' ? <Check size={12} /> : <Copy size={12} />}
                    {copiedId === 'example' ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <pre className="mt-code-body">{exampleCode}</pre>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}

const exampleCode = `curl -X POST ${SLM_BASE}/translate \\
  -H "Content-Type: application/json" \\
  -d '{
    "cobol_source": "MOVE WS-CUST-ID TO CM-CUST-ID\\nPERFORM READ-CUST",
    "language_pair": "cobol-java"
  }'`

export default ModelTrainingView
